import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HelpCircleIcon as HelpCircle, Cancel01Icon as X, ArrowDown01Icon as ChevronDown, Search01Icon as Search, Shield01Icon as ShieldCheck, VolumeHighIcon as Volume2, Cursor01Icon as MousePointer, Film01Icon as Film, ComputerIcon as Monitor } from 'hugeicons-react'

interface FaqItem {
    id: string
    question: string
    answer: string
    category: 'General' | 'Audio' | 'Cursor' | 'Privacy' | 'Export'
    icon: any
}

const FAQS: FaqItem[] = [
    {
        id: '1',
        category: 'General',
        icon: Monitor,
        question: 'Is ScreenRecord.ing completely free to use?',
        answer: 'Yes! ScreenRecord.ing is 100% free with no watermarks, no recording time limits, and no resolution caps. All video editing and export features are included.'
    },
    {
        id: '2',
        category: 'Audio',
        icon: Volume2,
        question: 'How do I record system or tab audio?',
        answer: 'When you click "Choose Screen & Start Recording", your browser will display a picker asking which tab or screen to share. Be sure to check the "Share audio" box at the bottom-left of that browser picker window!'
    },
    {
        id: '3',
        category: 'Cursor',
        icon: MousePointer,
        question: 'Can I enlarge or customize the mouse cursor?',
        answer: 'Yes! In the editor under the Cursor tab, you can choose custom cursor shapes (Circle, Ring, Arrow, Hand, Dot, Cross), increase cursor scale size up to 3x, change highlight halo colors, and enable click effects like Ripple and Pulse.'
    },
    {
        id: '4',
        category: 'Privacy',
        icon: ShieldCheck,
        question: 'Are my recordings private? Do they get uploaded anywhere?',
        answer: 'Your privacy is guaranteed. All video processing, timeline editing, canvas rendering, and file exporting happen 100% locally inside your web browser. Your recordings are never uploaded to any cloud server.'
    },
    {
        id: '5',
        category: 'Export',
        icon: Film,
        question: 'What video formats and resolutions can I export?',
        answer: 'You can export high-definition MP4, WebM, animated GIF, and image snapshots. You can also customize video canvas padding, round corners, gradient backgrounds, and device frames before exporting.'
    },
    {
        id: '6',
        category: 'General',
        icon: Monitor,
        question: 'What is the difference between the Web App and the Desktop App?',
        answer: 'The Web App runs right in your browser with zero installation. The Desktop App adds global system cursor tracking, whole-screen area selection, and native multi-monitor recording.'
    }
]

interface FaqModalProps {
    isOpen: boolean
    onClose: () => void
}

export default function FaqModal({ isOpen, onClose }: FaqModalProps) {
    const [search, setSearch] = useState('')
    const [expandedId, setExpandedId] = useState<string | null>('1')

    if (!isOpen) return null

    const filteredFaqs = FAQS.filter(
        f =>
            f.question.toLowerCase().includes(search.toLowerCase()) ||
            f.answer.toLowerCase().includes(search.toLowerCase()) ||
            f.category.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/80 backdrop-blur-md">
                {/* Backdrop Click */}
                <div className="absolute inset-0" onClick={onClose} />

                {/* Bottom to Top Full Page Opener Window Dialog */}
                <motion.div
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '100%', opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                    className="relative z-10 w-full max-w-6xl h-[92vh] mb-[-2px] bg-[#0A0A0C] border-t border-x border-white/10 rounded-t-[32px] shadow-2xl overflow-hidden flex flex-col"
                >
                    {/* Top Handle / Bar */}
                    <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mt-3 mb-1 sm:hidden" />

                    {/* Header */}
                    <div className="p-6 pb-4 border-b border-white/5 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 bg-brand-500/10 border border-brand-500/20 text-brand-400 rounded-2xl">
                                <HelpCircle size={22} />
                            </div>
                            <div>
                                <h3 className="text-xl font-display font-bold text-white">Frequently Asked Questions</h3>
                                <p className="text-xs text-txt-dim">Everything you need to know about ScreenRecord.ing</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-txt-dim hover:text-white hover:bg-white/10 rounded-full transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Search Bar */}
                    <div className="p-6 pb-3">
                        <div className="relative flex items-center">
                            <Search size={16} className="absolute left-3.5 text-txt-dim" />
                            <input
                                type="text"
                                placeholder="Search questions..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full bg-surface-900 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-xs text-white placeholder:text-txt-dim outline-none focus:border-brand-500 transition-colors"
                            />
                        </div>
                    </div>

                    {/* Accordion List */}
                    <div className="p-6 pt-0 overflow-y-auto space-y-3 flex-1 scrollbar-none">
                        {filteredFaqs.length > 0 ? (
                            filteredFaqs.map((faq) => {
                                const Icon = faq.icon
                                const isExpanded = expandedId === faq.id
                                return (
                                    <div
                                        key={faq.id}
                                        className="border border-white/5 rounded-2xl bg-white/[0.02] hover:bg-white/[0.04] transition-all overflow-hidden"
                                    >
                                        <button
                                            onClick={() => setExpandedId(isExpanded ? null : faq.id)}
                                            className="w-full p-4 flex items-center justify-between text-left gap-3"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 rounded-xl bg-white/5 text-brand-400 shrink-0">
                                                    <Icon size={16} />
                                                </div>
                                                <span className="text-sm font-semibold text-white">{faq.question}</span>
                                            </div>
                                            <ChevronDown
                                                size={18}
                                                className={`text-txt-dim transition-transform duration-300 shrink-0 ${isExpanded ? 'rotate-180 text-brand-400' : ''}`}
                                            />
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.25 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="px-4 pb-4 pt-1 text-xs text-txt-secondary leading-relaxed border-t border-white/5 ml-11">
                                                        {faq.answer}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center py-10 text-xs text-txt-dim border border-dashed border-white/5 rounded-2xl">
                                No questions found matching "{search}"
                            </div>
                        )}
                    </div>

                    {/* Modal Bottom Footer Notice */}
                    <div className="p-4 bg-surface-900/80 border-t border-white/5 text-center text-[11px] text-txt-dim">
                        Have a question not answered here? Click <strong className="text-brand-400">Feedback</strong> in the footer to contact us!
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
